import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { HeroImage } from '../common';
import { UnsplashCredit } from '../common/UnsplashCredit';
import { ConfirmDialog } from '../common';
import { ItineraryListItem } from '../../types';
import { deleteItinerary } from '../../services';
import './TripListCard.css';

interface TripListCardProps {
  trip: ItineraryListItem;
  onRemoved: (id: number) => void;
}

function computeCountdown(startDate: string | null, endDate: string | null, tripStatus?: string): string {
  if (!startDate) return 'dreaming';
  const now = new Date();
  const start = new Date(startDate + 'T00:00:00');
  const end = endDate ? new Date(endDate + 'T00:00:00') : start;

  if (now > end || tripStatus === 'completed') return 'done';

  const diffMs = start.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return 'now';
  if (diffDays === 1) return '1d';
  return `${diffDays}d`;
}

function formatTripDates(startDate: string | null, endDate: string | null): string | null {
  if (!startDate) return null;
  const start = new Date(startDate + 'T00:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

  if (!endDate || startDate === endDate) {
    return start.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  }

  const end = new Date(endDate + 'T00:00:00');
  if (start.getFullYear() === end.getFullYear()) {
    if (start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('en-US', opts)}-${end.getDate()}, ${start.getFullYear()}`;
    }
    return `${start.toLocaleDateString('en-US', opts)} - ${end.toLocaleDateString('en-US', opts)}, ${start.getFullYear()}`;
  }
  return `${start.toLocaleDateString('en-US', { ...opts, year: 'numeric' })} - ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

function computeDuration(startDate: string | null, endDate: string | null): string | null {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  const days = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return `${days} day${days !== 1 ? 's' : ''}`;
}

const TripListCard: React.FC<TripListCardProps> = ({ trip, onRemoved }) => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const countdown = computeCountdown(trip.start_date, trip.end_date, trip.trip_status);
  const dates = formatTripDates(trip.start_date, trip.end_date);
  const duration = computeDuration(trip.start_date, trip.end_date);
  const subtitle = [duration, trip.destination].filter(Boolean).join(' · ');

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await deleteItinerary(trip.id);
      onRemoved(trip.id);
    } catch {
      setRemoving(false);
    }
    setShowConfirm(false);
  };

  return (
    <>
      {/* Framed like the other credited cards (r776's closing note): the
          credit belonged to the card and sat as a sibling of it, so it was
          outside the card's own box and its hover surface. The card itself is
          a horizontal flex row, so the frame stacks them. */}
      <div className="trip-list-card-frame">
      <div className="trip-list-card" role="group">
        <button
          className="trip-list-card__main"
          onClick={() => navigate(`/itinerary/${trip.id}`)}
        >
          <HeroImage
            src={trip.hero_image_url}
            fallbackKey={trip.destination || trip.title}
            size="thumb-sm"
          />
          <div className="trip-list-card__body">
            <span className="trip-list-card__title">{trip.title}</span>
            {subtitle && <span className="trip-list-card__subtitle">{subtitle}</span>}
            {dates && <span className="trip-list-card__dates">{dates}</span>}
          </div>
          <span className={`trip-list-card__countdown ${countdown === 'dreaming' ? 'trip-list-card__countdown--dreaming' : ''}`}>
            {countdown === 'dreaming' ? (
              <em>dreaming</em>
            ) : (
              countdown
            )}
          </span>
        </button>
        <button
          className="trip-list-card__remove"
          aria-label="Remove trip"
          onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
        >
          <X size={14} />
        </button>
      </div>
      {/* Outside both buttons, beside the thumbnail it credits (r774): an <a>
          nested in a <button> is invalid markup and the photographer link
          would not be reachable, which is the same as not crediting. */}
      {trip.hero_image_url && trip.hero_attribution && (
        <UnsplashCredit
          photographer={trip.hero_attribution.photographer}
          profileLink={trip.hero_attribution.unsplash_link}
          className="card-photo-credit"
        />
      )}
      </div>

      {showConfirm && (
        <ConfirmDialog
          isOpen={true}
          title={`Remove ${trip.title}?`}
          message="It comes off your plans, and so does its copy on Explore if it has one. You can't get it back from the app."
          confirmText={removing ? 'Removing...' : 'Remove'}
          cancelText="Keep it"
          onConfirm={handleRemove}
          onClose={() => setShowConfirm(false)}
          variant="danger"
          isLoading={removing}
        />
      )}
    </>
  );
};

export default TripListCard;
